/**
 * Marketing Assets Library — platform-aware collateral store.
 *
 * - Folders: ALL · INSTAGRAM · REEL/TIKTOK · FACEBOOK · LINKEDIN · WHATSAPP · TWITTER/X
 * - Assets are uploaded by admins (drag & drop) OR by Claude AI via MCP
 *   (`save_marketing_asset_from_url`).
 * - Each card supports preview, download, and admin-only delete.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, Download, Trash2, FileText, ImageIcon,
  Video, Sparkles, Loader2, Bot, Plus,
} from 'lucide-react';

const PLATFORMS = [
  { key: 'all',         label: 'ALL',          icon: FolderOpen },
  { key: 'instagram',   label: 'INSTAGRAM',    icon: ImageIcon },
  { key: 'reel_tiktok', label: 'REEL/TIKTOK',  icon: Video },
  { key: 'facebook',    label: 'FACEBOOK',     icon: ImageIcon },
  { key: 'linkedin',    label: 'LINKEDIN',     icon: ImageIcon },
  { key: 'whatsapp',    label: 'WHATSAPP',     icon: ImageIcon },
  { key: 'twitter',     label: 'TWITTER/X',    icon: ImageIcon },
  { key: 'internal',    label: 'INTERNAL',     icon: FileText },
];

const AUDIENCES = [
  { key: 'students', label: 'Students' },
  { key: 'agents', label: 'Agents' },
  { key: 'universities', label: 'Universities' },
  { key: 'internal', label: 'Internal' },
];

const PREVIEW_MIMES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function platformBadgeColor(p) {
  return {
    instagram:   'bg-pink-100 text-pink-700 border-pink-300',
    reel_tiktok: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
    facebook:    'bg-blue-100 text-blue-700 border-blue-300',
    linkedin:    'bg-sky-100 text-sky-700 border-sky-300',
    whatsapp:    'bg-emerald-100 text-emerald-700 border-emerald-300',
    twitter:     'bg-slate-100 text-slate-700 border-slate-300',
    internal:    'bg-amber-100 text-amber-700 border-amber-300',
  }[p] || 'bg-gray-100 text-gray-700 border-gray-300';
}

export default function SocialAssetsLibrary() {
  const [items, setItems] = useState([]);
  const [tallies, setTallies] = useState({});
  const [activePlatform, setActivePlatform] = useState('all');
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async (platform) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/marketing/assets', {
        params: platform && platform !== 'all' ? { platform } : {},
      });
      setItems(res.data.items || []);
      setTallies(res.data.tallies || {});
    } catch (e) {
      toast.error('Failed to load marketing library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activePlatform); }, [activePlatform, load]);

  const handleDownload = async (asset) => {
    try {
      const res = await api.get(`/admin/marketing/assets/${asset.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = asset.filename || `${asset.title}.${asset.format || 'bin'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/marketing/assets/${asset.id}`);
      toast.success('Deleted');
      load(activePlatform);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Card data-testid="social-assets-library">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          Social Assets Library
          <span className="ml-2 text-xs font-normal text-gray-500">
            Folders for every platform — drag & drop, or let Claude AI file them for you.
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* PLATFORM FILTER BUTTONS */}
        <div className="flex flex-wrap gap-2 mb-6">
          {PLATFORMS.map(({ key, label, icon: Icon }) => {
            const count = tallies[key] ?? 0;
            const active = activePlatform === key;
            return (
              <button
                key={key}
                onClick={() => setActivePlatform(key)}
                data-testid={`platform-tab-${key}`}
                className={
                  'flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold tracking-wider transition-all ' +
                  (active
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50')
                }
              >
                <Icon className="w-4 h-4" />
                {label}
                {count > 0 && (
                  <span className={
                    'inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1 rounded-full text-xs ' +
                    (active ? 'bg-white text-indigo-700' : 'bg-indigo-100 text-indigo-700')
                  }>{count}</span>
                )}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <UploadDialog
              open={uploadOpen}
              onOpenChange={setUploadOpen}
              onUploaded={() => load(activePlatform)}
              defaultPlatform={activePlatform === 'all' ? 'instagram' : activePlatform}
            />
          </div>
        </div>

        {/* CLAUDE HINT */}
        <div className="mb-4 flex items-start gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm text-indigo-900">
          <Bot className="w-5 h-5 flex-shrink-0 mt-0.5 text-indigo-600" />
          <div>
            <strong>Connected to Claude AI?</strong>{' '}
            Two ways to file assets: (1) Give Claude a public URL and it saves it via
            {' '}<code className="px-1.5 py-0.5 bg-white border border-indigo-200 rounded text-xs">
              save_marketing_asset_from_url
            </code>. (2) Or tell Claude what you want to upload — it will hand you a
            one-time upload link so you can drag & drop the file from your computer.
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="py-16 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library…
          </div>
        ) : items.length === 0 ? (
          <EmptyState platform={activePlatform} onUpload={() => setUploadOpen(true)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                onDownload={() => handleDownload(a)}
                onDelete={() => handleDelete(a)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AssetCard({ asset, onDownload, onDelete }) {
  const isImage = PREVIEW_MIMES.includes((asset.format || '').toLowerCase());
  const previewSrc = asset.preview_url
    ? `${api.defaults.baseURL || ''}${asset.preview_url}`
    : null;
  // Add token to preview src so the <img> tag can authenticate
  const token = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('token')
    : null;
  const imgSrc = previewSrc && token ? `${previewSrc}?_t=${encodeURIComponent(token)}` : previewSrc;
  return (
    <div
      className="border rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow flex flex-col"
      data-testid={`asset-card-${asset.id}`}
    >
      <div className="aspect-square bg-gray-50 relative flex items-center justify-center border-b">
        {isImage ? (
          <AuthImage assetId={asset.id} alt={asset.title} />
        ) : asset.format === 'pdf' ? (
          <div className="flex flex-col items-center gap-2 text-rose-600">
            <FileText className="w-12 h-12" />
            <span className="text-xs font-bold uppercase">PDF</span>
          </div>
        ) : asset.format === 'mp4' || asset.format === 'mov' || asset.format === 'webm' ? (
          <div className="flex flex-col items-center gap-2 text-purple-600">
            <Video className="w-12 h-12" />
            <span className="text-xs font-bold uppercase">{asset.format}</span>
          </div>
        ) : (
          <FileText className="w-12 h-12 text-gray-400" />
        )}
        <span className={
          'absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ' +
          platformBadgeColor(asset.platform)
        }>{(asset.platform || 'internal').replace('_', '/')}</span>
        {asset.created_by_name === 'Claude (MCP)' && (
          <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-600 text-white flex items-center gap-1">
            <Bot className="w-3 h-3" /> CLAUDE
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <h4 className="font-medium text-sm text-gray-900 line-clamp-2" title={asset.title}>{asset.title}</h4>
        <p className="text-xs text-gray-500 mt-1">
          {asset.format?.toUpperCase()} · {fmtBytes(asset.size_bytes)} · {asset.audience}
        </p>
        {asset.description && (
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{asset.description}</p>
        )}
        <div className="mt-auto pt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onDownload}
            className="flex-1 h-8 text-xs"
            data-testid={`asset-download-${asset.id}`}
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`asset-delete-${asset.id}`}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Tiny helper that loads the preview through axios (so the Authorization
    header gets attached automatically), then renders it as a blob URL.  */
function AuthImage({ assetId, alt }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    let blobUrl = null;
    (async () => {
      try {
        const res = await api.get(`/admin/marketing/assets/${assetId}/preview`, {
          responseType: 'blob',
        });
        if (!alive) return;
        blobUrl = URL.createObjectURL(res.data);
        setSrc(blobUrl);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [assetId]);
  if (err) return <ImageIcon className="w-12 h-12 text-gray-300" />;
  if (!src) return <Loader2 className="w-6 h-6 animate-spin text-gray-300" />;
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
}

function EmptyState({ platform, onUpload }) {
  return (
    <div className="py-12 px-4 text-center border-2 border-dashed border-gray-200 rounded-xl">
      <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
      <h4 className="font-semibold text-gray-700">
        No assets in {platform === 'all' ? 'the library' : `the ${platform.replace('_', '/')} folder`} yet
      </h4>
      <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
        Upload manually below, or ask Claude AI:{' '}
        <em>{`"Save this image at https://… as an Instagram post titled 'Spring 2026 intake'."`}</em>
      </p>
      <Button onClick={onUpload} className="mt-4" data-testid="empty-upload-btn">
        <Upload className="w-4 h-4 mr-2" /> Upload your first asset
      </Button>
    </div>
  );
}

function UploadDialog({ open, onOpenChange, onUploaded, defaultPlatform = 'instagram' }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState(defaultPlatform);
  const [audience, setAudience] = useState('students');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (open) setPlatform(defaultPlatform === 'all' ? 'instagram' : defaultPlatform); },
    [defaultPlatform, open]);

  const reset = () => {
    setFile(null); setTitle(''); setDescription(''); setTags(''); setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!file) return toast.error('Pick a file to upload');
    if (!title.trim()) return toast.error('Title is required');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('platform', platform);
      fd.append('audience', audience);
      fd.append('description', description);
      fd.append('tags', tags);
      await api.post('/admin/marketing/assets/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Asset uploaded');
      reset();
      onOpenChange(false);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogTrigger asChild>
        <Button data-testid="upload-asset-btn" className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Upload asset
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload a marketing asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="upl-file">File (max 25 MB)</Label>
            <Input
              ref={fileRef}
              id="upl-file"
              type="file"
              accept="image/*,application/pdf,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            {file && <p className="text-xs text-gray-500 mt-1">{file.name} · {fmtBytes(file.size)}</p>}
          </div>
          <div>
            <Label htmlFor="upl-title">Title</Label>
            <Input
              id="upl-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Instagram Square — Spring 2026 intake"
              data-testid="upload-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="upload-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.filter(p => p.key !== 'all').map(p => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger data-testid="upload-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="upl-desc">Description (optional)</Label>
            <Textarea
              id="upl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short blurb…"
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="upl-tags">Tags (comma-separated)</Label>
            <Input
              id="upl-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="UK, spring-2026, scholarship"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="upload-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
